import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import CommonService from "../../services/common.service";
import { LOGISTICS_TRANSPORT_MODES } from "../../constants/logisticsProfileFormOptions";
import { useCurrencyMaster } from "../../hooks/useCurrencyMaster";
import { usePaymentTermsMaster } from "../../hooks/usePaymentTermsMaster";
import { legacyPreferredPaymentTermsObjectToLabels } from "../website/logisticsGenericRfqFormState";
import { buildLogisticsViewRfqPathFromIds } from "../website/logisticsRfqSubmissionStorage";
import { resolveLogisticsRfqRequestorInfo } from "../../utils/logisticsRfqRequestorDisplay";
import {
  isLogisticsQuoteFieldRequired,
  validateLogisticsQuoteForm,
} from "../../utils/logisticsQuoteFormValidation";
import LogisticsQuoteSectionIcon from "../../components/logistics/LogisticsQuoteSectionIcon";
import "../../assets/styles/logistics_quote_modal.scss";
import "../../assets/styles/logistics_provider_rfqs_received.scss";
import "../../assets/styles/logistics_rfq_submitted_view.scss";

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function normalizeQuoteStatus(status) {
  if (status == null) return null;
  const s = String(status).trim().toLowerCase();
  return s || null;
}

/** Editable only while draft (or before first save). */
function isQuoteLocked(status) {
  const s = normalizeQuoteStatus(status);
  return s === "submitted" || s === "accepted" || s === "rejected" || s === "pending_approval";
}

function quotePageHeading(status) {
  return isQuoteLocked(status) ? "View Quote" : "Submit Quote";
}

function quoteStatusDisplayLabel(status) {
  const s = normalizeQuoteStatus(status);
  if (!s) return null;
  if (s === "accepted") return "Accepted";
  if (s === "submitted") return "Submitted";
  if (s === "pending_approval") return "Pending Logistics Approval";
  if (s === "rejected") return "Rejected";
  if (s === "draft") return "Draft";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function transportModeLabel(modeId) {
  if (modeId == null || String(modeId).trim() === "") return null;
  const id = String(modeId).trim().toLowerCase();
  const m = LOGISTICS_TRANSPORT_MODES.find((x) => x.id === id);
  return m ? m.label : String(modeId);
}

const RELOCATION_SCOPE_OPTIONS = [
  { value: "office", label: "Office Relocation" },
  { value: "employee", label: "Employee Relocation" },
  { value: "both", label: "Both Office & Employee" },
];

function relocationScopeLabel(scope) {
  const s = scope != null ? String(scope).trim().toLowerCase() : "";
  const o = RELOCATION_SCOPE_OPTIONS.find((x) => x.value === s);
  return o ? o.label : scope ? String(scope) : null;
}

function mapCountriesResponse(response) {
  const apiCountries = Array.isArray(response?.data)
    ? response.data
    : response?.data?.countries || response?.data?.master_countries || [];
  return apiCountries
    .map((country) => {
      const id = country.country_id ?? country.id ?? country.value;
      const label =
        country.country_name ?? country.country_label ?? country.label ?? country.name ?? country.country;
      return { id: id != null ? String(id) : "", label: label != null ? String(label) : "" };
    })
    .filter((c) => c.id && c.label);
}

function mapStatesResponse(response) {
  const apiStates = Array.isArray(response?.data)
    ? response.data
    : response?.data?.states || response?.data?.master_states || [];
  return apiStates
    .map((state) => {
      const id = state.state_id ?? state.id ?? state.value;
      const label = state.state_name ?? state.state_label ?? state.label ?? state.name ?? state.state;
      return { id: id != null ? String(id) : "", label: label != null ? String(label) : "" };
    })
    .filter((s) => s.id && s.label);
}

function mapCitiesResponse(response) {
  const apiCities = Array.isArray(response?.data)
    ? response.data
    : response?.data?.cities || response?.data?.master_cities || [];
  return apiCities
    .map((city) => {
      const id = city.city_id ?? city.id ?? city.value;
      const label = city.city_name ?? city.city_label ?? city.label ?? city.name ?? city.city;
      return { id: id != null ? String(id) : "", label: label != null ? String(label) : "" };
    })
    .filter((c) => c.id && c.label);
}

async function resolveLocationLabels(countryId, stateId, cityId) {
  const cid = countryId != null && String(countryId).trim() !== "" ? String(countryId).trim() : "";
  const sid = stateId != null && String(stateId).trim() !== "" ? String(stateId).trim() : "";
  const cyid = cityId != null && String(cityId).trim() !== "" ? String(cityId).trim() : "";
  if (!cid) return { country: "—", state: "—", city: "—" };
  try {
    const cRes = await CommonService.get("/master/company-Country");
    const countries = mapCountriesResponse(cRes);
    const countryLabel = countries.find((c) => c.id === cid)?.label || cid;

    let stateLabel = "—";
    if (sid) {
      const sRes = await CommonService.get(`/master/company-State/${cid}`);
      const states = mapStatesResponse(sRes);
      stateLabel = states.find((s) => s.id === sid)?.label || sid;
    }

    let cityLabel = "—";
    if (cyid && sid) {
      const cityRes = await CommonService.get(`/master/company-City/${sid}`);
      const cities = mapCitiesResponse(cityRes);
      cityLabel = cities.find((c) => c.id === cyid)?.label || cyid;
    }

    return { country: countryLabel, state: stateLabel, city: cityLabel };
  } catch {
    return { country: cid, state: sid || "—", city: cyid || "—" };
  }
}

function formatExpoDate(v) {
  if (v == null || String(v).trim() === "") return "—";
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    try {
      const d = new Date(`${s}T12:00:00`);
      if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
    } catch {
      /* fall through */
    }
  }
  try {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
  } catch {
    /* ignore */
  }
  return s;
}

function formatSummaryWeight(p) {
  const unit =
    p.totalWeightUnit != null && String(p.totalWeightUnit).trim() !== ""
      ? String(p.totalWeightUnit).trim()
      : "kg";
  const value =
    p.totalWeightValue != null && String(p.totalWeightValue).trim() !== ""
      ? p.totalWeightValue
      : p.totalWeightKg != null && String(p.totalWeightKg).trim() !== ""
        ? p.totalWeightKg
        : null;
  return value != null ? `${value} ${unit}`.trim() : "—";
}

function formatSummaryVolume(p) {
  const unit =
    p.totalVolumeUnit != null && String(p.totalVolumeUnit).trim() !== ""
      ? String(p.totalVolumeUnit).trim()
      : "CBM";
  const value =
    p.totalVolumeValue != null && String(p.totalVolumeValue).trim() !== ""
      ? p.totalVolumeValue
      : p.totalVolumeCbm != null && String(p.totalVolumeCbm).trim() !== ""
        ? p.totalVolumeCbm
        : null;
  return value != null ? `${value} ${unit}`.trim() : "—";
}

function rfqSummaryFromPayload(r) {
  const p = r?.payload && typeof r.payload === "object" ? r.payload : {};
  const kind = r?.rfqKind != null ? String(r.rfqKind).trim().toLowerCase() : "generic";

  const weight = formatSummaryWeight(p);
  let volume = formatSummaryVolume(p);

  const labels = {
    origin: "Origin",
    destination: "Destination",
    cargoType: "Cargo Type",
    container: "Container",
    weight: "Weight",
    volume: "Volume",
    incoterm: "Incoterms",
    expectedDate: "Expected Date",
    requirements: "Requirements",
  };

  let origin = "—";
  let destination = "—";
  let cargoType = "—";
  let container = "—";
  let incoterm = "—";
  let expectedDate = "—";
  let requirements = "—";

  const preferredPaymentTerms = (() => {
    const raw = p.preferredPaymentTerms;
    if (Array.isArray(raw)) {
      const s = raw.map((x) => String(x).trim()).filter(Boolean);
      return s.length ? s.join(", ") : null;
    }
    if (raw && typeof raw === "object") {
      const labels = legacyPreferredPaymentTermsObjectToLabels(raw);
      return labels.length ? labels.join(", ") : null;
    }
    return null;
  })();

  if (kind === "business-relocation") {
    labels.origin = "Current location";
    labels.destination = "Destination";
    labels.cargoType = "Service";
    labels.container = "Type of Relocation";
    labels.incoterm = "Logistics mode";
    labels.expectedDate = "Preferred move date";
    labels.requirements = "Notes";
    labels.volume = "Budget Range (USD)";

    origin = r?.resolvedLocations?.origin || "—";
    destination = r?.resolvedLocations?.destination || "—";
    cargoType = "Business relocation";
    container = relocationScopeLabel(p.relocationScope) || "—";
    incoterm = p.logisticsMode || "—";
    expectedDate = p.preferredMoveDate || "—";
    requirements = p.specialNotes || "—";
    volume = p.budgetRange || "—";
  } else if (kind === "exhibition-logistics") {
    labels.destination = "Expo venue";
    labels.cargoType = "Materials";
    labels.container = "Packaging / Booth";
    labels.incoterm = "Logistics mode";
    labels.expectedDate = "Expo start date";
    labels.requirements = "Special handling";
    labels.volume = "Urgency level";

    origin = r?.resolvedLocations?.origin || "—";
    destination = r?.resolvedLocations?.destination || "—";
    cargoType = p.materialsDescription || "Exhibition materials";
    container = p.packagingType || p.boothNumber || "—";
    incoterm = p.logisticsMode || "—";
    expectedDate = p.exhibitionStartDate || p.setupDate || "—";
    requirements = p.specialHandling || "—";
    volume = p.urgencyLevel || "—";
  } else {
    labels.expectedDate = "Expected date";
    labels.requirements = "Preferred payment terms";

    origin = r?.resolvedLocations?.origin || "—";
    destination = r?.resolvedLocations?.destination || "—";
    cargoType = p.commodityType || p.cargoDescription || "—";
    container = p.modeDetail || "—";
    incoterm = p.incoterm || "—";
    expectedDate = p.deliveryDate || p.pickupDate || "—";
    requirements = preferredPaymentTerms || p.creditTerms || p.paymentMode || "—";
  }

  return { labels, origin, destination, cargoType, container, weight, volume, incoterm, expectedDate, requirements };
}

export default function LogisticsProviderQuoteSubmissionPage() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { currencyOptions, loading: currencyLoading } = useCurrencyMaster();
  const { paymentTermSelectOptions: paymentTermMasterOptions, loading: paymentTermsLoading } =
    usePaymentTermsMaster();

  const [loading, setLoading] = useState(true);
  const [rfq, setRfq] = useState(null);
  const [resolvedLocations, setResolvedLocations] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadedQuoteStatus, setLoadedQuoteStatus] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const quoteLocked = useMemo(() => isQuoteLocked(loadedQuoteStatus), [loadedQuoteStatus]);
  const pageHeading = useMemo(() => quotePageHeading(loadedQuoteStatus), [loadedQuoteStatus]);
  const statusLabel = useMemo(() => quoteStatusDisplayLabel(loadedQuoteStatus), [loadedQuoteStatus]);
  const requestorInfo = useMemo(() => resolveLogisticsRfqRequestorInfo(rfq), [rfq]);

  const pageSubtitle = useMemo(() => {
    if (quoteLocked) return "Review your submitted quote for this RFQ.";
    const kind = rfq?.rfqKind != null ? String(rfq.rfqKind).trim().toLowerCase() : "";
    if (kind === "exhibition-logistics") {
      return "Prepare and submit your quote for this exhibition logistics RFQ.";
    }
    if (kind === "business-relocation") {
      return "Prepare and submit your quote for this business relocation RFQ.";
    }
    return "Prepare and submit your quote for the assigned logistics RFQ.";
  }, [quoteLocked, rfq?.rfqKind]);

  const [form, setForm] = useState(() => ({
    currency: "USD",
    validityDays: 30,
    transitDays: 12,
    quoteExpiryDate: "",
    vesselName: "",
    voyageNumber: "",
    estDeparture: "",
    estArrival: "",
    oceanFreight: "",
    fuelSurcharge: "",
    portHandling: "",
    documentation: "",
    containerDeposit: "",
    loadingCharge: "",
    loadingIncluded: true,
    loadingLocation: "Origin Port",
    unloadingCharge: "",
    unloadingIncluded: true,
    unloadingLocation: "Destination Port",
    cargoInsuranceOn: false,
    insuranceAmount: "",
    coveragePercent: "",
    coverageType: "Full Cargo Value",
    customsOn: false,
    customsClearanceCharge: "",
    warehousingOn: false,
    freeDaysIncluded: "",
    baseStorageCharge: "",
    perDayChargeAfterFree: "",
    pickupOn: false,
    pickupLocation: "",
    pickupCharge: "",
    deliveryOn: false,
    deliveryLocation: "",
    deliveryCharge: "",
    freeDaysOrigin: "",
    freeDaysDestination: "",
    detentionPerDay: "",
    demurragePerDay: "",
    paymentTerms: "50% advance, 50% before shipment",
    exclusions: "Customs duties and taxes, Destination port charges, Inland transport",
    specialTerms: "",
    additionalNotes: "",
    generalTerms: "Subject to vessel space availability. Rates valid for immediate booking only.",
  }));

  const currencySelectOptions =
    Array.isArray(currencyOptions) && currencyOptions.length
      ? currencyOptions
          .map((o) => ({
            value: (o.currencyCode || o.value) != null ? String(o.currencyCode || o.value) : "",
            label: o.label,
          }))
          .filter((o) => o.value && o.label)
      : [
          { value: "USD", label: "USD ($)" },
          { value: "INR", label: "INR (₹)" },
          { value: "EUR", label: "EUR (€)" },
        ];

  const paymentTermSelectOptions = useMemo(() => {
    const base =
      Array.isArray(paymentTermMasterOptions) && paymentTermMasterOptions.length
        ? paymentTermMasterOptions
        : [];
    const current = String(form.paymentTerms || "").trim();
    if (current && !base.some((opt) => opt.value === current)) {
      return [{ value: current, label: current }, ...base];
    }
    return base.length
      ? base
      : current
        ? [{ value: current, label: current }]
        : [{ value: "50% advance, 50% before shipment", label: "50% advance, 50% before shipment" }];
  }, [paymentTermMasterOptions, form.paymentTerms]);

  useEffect(() => {
    setFieldErrors((prev) => {
      if (!Object.keys(prev).length) return prev;
      const { fieldErrors: nextErrors } = validateLogisticsQuoteForm(form);
      const next = {};
      Object.keys(prev).forEach((key) => {
        if (nextErrors[key]) next[key] = nextErrors[key];
      });
      const unchanged =
        Object.keys(prev).length === Object.keys(next).length &&
        Object.keys(prev).every((key) => prev[key] === next[key]);
      return unchanged ? prev : next;
    });
  }, [form]);

  const fieldWrap = (fieldKey) => ({
    className: fieldErrors[fieldKey] ? "lq-field-box lq-field-box--error" : "lq-field-box",
    "data-quote-field": fieldKey,
  });

  const FieldError = ({ fieldKey }) =>
    fieldErrors[fieldKey] ? <div className="lq-field-error">{fieldErrors[fieldKey]}</div> : null;

  const MandatoryMark = ({ fieldKey }) =>
    isLogisticsQuoteFieldRequired(fieldKey, form) ? (
      <span className="lq-field-req" aria-hidden="true">
        *
      </span>
    ) : null;

  const totalQuoteAmount = useMemo(() => {
    return (
      toNum(form.oceanFreight) +
      toNum(form.fuelSurcharge) +
      toNum(form.portHandling) +
      toNum(form.documentation) +
      toNum(form.containerDeposit) +
      toNum(form.loadingCharge) +
      toNum(form.unloadingCharge) +
      toNum(form.customsClearanceCharge) +
      toNum(form.baseStorageCharge) +
      toNum(form.perDayChargeAfterFree) +
      toNum(form.pickupCharge) +
      toNum(form.deliveryCharge)
    );
  }, [form]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const idNum = submissionId != null ? parseInt(String(submissionId), 10) : NaN;
        if (!Number.isFinite(idNum) || idNum <= 0) throw new Error("Invalid submission id");
        const res = await CommonService.get(`/company/logistics-providers/rfq/submission/${idNum}`);
        const body = res?.data ?? res ?? {};
        const data = body?.data ?? body;
        if (cancelled) return;
        setRfq(data);
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || "Could not load RFQ.");
        if (!cancelled) setRfq(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [submissionId]);

  // Load existing draft/submitted quote (if any)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const idNum = submissionId != null ? parseInt(String(submissionId), 10) : NaN;
        if (!Number.isFinite(idNum) || idNum <= 0) return;
        const res = await CommonService.get(`/company/logistics-providers/rfq/submission/${idNum}/quote`);
        const body = res?.data ?? res ?? {};
        const data = body?.data ?? body;
        const wrapper = data?.quote || null;
        const q = wrapper?.quote ?? wrapper?.quote_json ?? null;
        const status = normalizeQuoteStatus(wrapper?.status ?? data?.status);
        if (cancelled) return;
        setLoadedQuoteStatus(status);
        if (q && typeof q === "object" && !Array.isArray(q)) {
          setForm((p) => ({ ...p, ...q }));
        }
      } catch {
        // ignore (no draft yet)
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [submissionId]);

  useEffect(() => {
    let cancelled = false;
    if (!rfq?.payload) return undefined;
    const p = rfq.payload;
    const kind = rfq?.rfqKind != null ? String(rfq.rfqKind).trim().toLowerCase() : "generic";
    const pick = () => {
      if (kind === "business-relocation") {
        return {
          origin: { c: p.currentCountry, s: p.currentState, cy: p.currentCity },
          destination: { c: p.destCountry, s: p.destState, cy: p.destCity },
        };
      }
      if (kind === "exhibition-logistics") {
        return {
          origin: { c: p.orgCountry, s: p.orgState, cy: p.orgCity },
          destination: { c: p.venueCountry, s: p.venueState, cy: p.venueCity },
        };
      }
      return {
        origin: { c: p.originCountry, s: p.originState, cy: p.originCity },
        destination: { c: p.destCountry, s: p.destState, cy: p.destCity },
      };
    };
    (async () => {
      const sel = pick();
      const [o, d] = await Promise.all([
        resolveLocationLabels(sel.origin.c, sel.origin.s, sel.origin.cy),
        resolveLocationLabels(sel.destination.c, sel.destination.s, sel.destination.cy),
      ]);
      if (cancelled) return;
      const join = (x) => [x.city, x.state, x.country].filter(Boolean).join(", ") || "—";
      setResolvedLocations({ origin: join(o), destination: join(d) });
    })();
    return () => {
      cancelled = true;
    };
  }, [rfq?.id]);

  // Auto-populate Origin/Destination Port locations from RFQ (without overwriting user edits)
  useEffect(() => {
    if (!resolvedLocations?.origin && !resolvedLocations?.destination) return;
    setForm((p) => {
      const origin = resolvedLocations?.origin || "";
      const destination = resolvedLocations?.destination || "";

      const shouldFillOrigin =
        (!p.loadingLocation || String(p.loadingLocation).trim() === "" || p.loadingLocation === "Origin Port") && origin;
      const shouldFillDest =
        (!p.unloadingLocation ||
          String(p.unloadingLocation).trim() === "" ||
          p.unloadingLocation === "Destination Port") &&
        destination;

      if (!shouldFillOrigin && !shouldFillDest) return p;
      return {
        ...p,
        loadingLocation: shouldFillOrigin ? origin : p.loadingLocation,
        unloadingLocation: shouldFillDest ? destination : p.unloadingLocation,
      };
    });
  }, [resolvedLocations?.origin, resolvedLocations?.destination]);

  const summary = rfqSummaryFromPayload({ ...rfq, resolvedLocations });

  const buildQuotePayload = () => {
    return {
      ...form,
      totalAmount: Math.max(0, totalQuoteAmount),
    };
  };

  const handleSaveDraft = async () => {
    try {
      if (quoteLocked) {
        toast.info("This quote can no longer be edited.");
        return;
      }
      const idNum = submissionId != null ? parseInt(String(submissionId), 10) : NaN;
      if (!Number.isFinite(idNum) || idNum <= 0) throw new Error("Invalid submission id");
      setSavingDraft(true);
      await CommonService.post(`/company/logistics-providers/rfq/submission/${idNum}/quote/draft`, {
        quote: buildQuotePayload(),
      });
      toast.success("Draft saved successfully.");
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Could not save draft.");
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmitQuote = async () => {
    try {
      if (quoteLocked) {
        toast.info("This quote has already been submitted.");
        return;
      }
      const idNum = submissionId != null ? parseInt(String(submissionId), 10) : NaN;
      if (!Number.isFinite(idNum) || idNum <= 0) throw new Error("Invalid submission id");

      const validation = validateLogisticsQuoteForm(form);
      if (!validation.valid) {
        setFieldErrors(validation.fieldErrors);
        const preview = validation.missingLabels.slice(0, 5).join(", ");
        toast.error(
          `Please fill all mandatory fields${preview ? `: ${preview}${validation.missingLabels.length > 5 ? "…" : ""}` : ""}`,
        );
        requestAnimationFrame(() => {
          document.querySelector("[data-quote-field].lq-field-box--error")?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        });
        return;
      }
      setFieldErrors({});

      const q = buildQuotePayload();
      setSubmitting(true);
      const resp = await CommonService.post(`/company/logistics-providers/rfq/submission/${idNum}/quote/submit`, {
        quote: q,
      });
      const quoteApproval = resp?.data?.quoteApproval;
      if (quoteApproval?.mode === "pending") {
        toast.info(
          "Quote submitted for P2P logistics approval. The buyer can compare quotes after approvers complete the workflow.",
        );
        setLoadedQuoteStatus("pending_approval");
      } else {
        toast.success("Quote submitted successfully.");
        setLoadedQuoteStatus("submitted");
      }
      navigate("/dashboard/logistics/rfqs");
    } catch (e) {
      const resp = e?.response?.data;
      if (resp?.errors && Array.isArray(resp.errors) && resp.errors.length) {
        toast.error(resp.errors[0]);
      } else {
        toast.error(resp?.message || e?.message || "Could not submit quote.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lq-page lprfq-page">
      <div className="container-fluid py-4">
        <div className="lrfv-dashboard-topbar lq-dashboard-topbar">
          <nav aria-label="breadcrumb" className="lprfq-breadcrumb lrfv-dashboard-breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to="/dashboard/logistics">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/dashboard/logistics/rfqs">RFQs Received</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {pageHeading}
              </li>
            </ol>
          </nav>
          <Link to="/dashboard/logistics/rfqs" className="lrfv-back lrfv-back--dashboard">
            ← Back to received RFQs
          </Link>
        </div>

        <header className="lq-page-hero">
          <div className="lq-page-hero__copy">
            <h1 className="lprfq-hero__title">{pageHeading}</h1>
            <p className="lprfq-hero__subtitle">{pageSubtitle}</p>
          </div>
          <div className="lq-chip-row lq-page-hero__chips">
            {rfq?.id
              ? (() => {
                  const viewPath = buildLogisticsViewRfqPathFromIds(
                    rfq.logisticsProviderCompanyId,
                    rfq.id,
                    rfq.rfqReferenceNumber,
                    "logistics-inbox",
                    rfq.rfqKind,
                  );
                  return viewPath ? (
                    <Link to={viewPath} className="lq-chip text-decoration-none">
                      View Full RFQ
                    </Link>
                  ) : null;
                })()
              : null}
            <span className="lq-chip">RFQ {rfq?.rfqReferenceNumber || "—"}</span>
            {statusLabel ? <span className="lq-chip lq-chip--status">{statusLabel}</span> : null}
          </div>
        </header>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : !rfq ? (
        <div className="alert alert-danger">Could not load RFQ.</div>
      ) : (
        <div className="lq-main-grid">
          <div>
            {/* RFQ Summary (must remain unchanged) */}
            <div className="lq-card lq-card--summary">
              <div className="lq-card-head lq-card-head--summary">
                <LogisticsQuoteSectionIcon name="rfqSummary" /> RFQ Summary
              </div>
              <div className="lq-card-body">
                <div className="lq-kv-grid">
                  <div className="lq-kv">
                    <div className="lq-k">{summary.labels?.origin || "Origin"}</div>
                    <div className="lq-v">{summary.origin}</div>
                  </div>
                  <div className="lq-kv">
                    <div className="lq-k">{summary.labels?.destination || "Destination"}</div>
                    <div className="lq-v">{summary.destination}</div>
                  </div>
                  <div className="lq-kv">
                    <div className="lq-k">{summary.labels?.cargoType || "Cargo Type"}</div>
                    <div className="lq-v">{summary.cargoType}</div>
                  </div>
                  <div className="lq-kv">
                    <div className="lq-k">{summary.labels?.container || "Container"}</div>
                    <div className="lq-v">{summary.container}</div>
                  </div>
                  <div className="lq-kv">
                    <div className="lq-k">{summary.labels?.weight || "Weight"}</div>
                    <div className="lq-v">{summary.weight}</div>
                  </div>
                  <div className="lq-kv">
                    <div className="lq-k">{summary.labels?.volume || "Volume"}</div>
                    <div className="lq-v">{summary.volume}</div>
                  </div>
                  <div className="lq-kv">
                    <div className="lq-k">{summary.labels?.incoterm || "Incoterms"}</div>
                    <div className="lq-v">{summary.incoterm}</div>
                  </div>
                  <div className="lq-kv">
                    <div className="lq-k">{summary.labels?.expectedDate || "Expected Date"}</div>
                    <div className="lq-v">{formatExpoDate(summary.expectedDate)}</div>
                  </div>
                </div>
                <div className="lq-muted-box">
                  <div className="fw-bold mb-1">{summary.labels?.requirements || "Requirements"}:</div>
                  <div className="lq-muted-box__content">{String(summary.requirements)}</div>
                </div>
              </div>
            </div>

            {/* Requestor Info */}
            <div className="lq-card lq-card--provider">
              <div className="lq-card-head lq-card-head--provider">
                <LogisticsQuoteSectionIcon name="provider" /> Requestor Information
              </div>
              <div className="lq-card-body">
                <div className="lq-kv-grid lq-kv-grid--provider">
                  <div className="lq-kv">
                    <div className="lq-k">Name</div>
                    <div className="lq-v">{requestorInfo?.name || "—"}</div>
                  </div>
                  <div className="lq-kv">
                    <div className="lq-k">Contact Number</div>
                    <div className="lq-v">{requestorInfo?.contactNumber || "—"}</div>
                  </div>
                  <div className="lq-kv">
                    <div className="lq-k">Email</div>
                    <div className="lq-v">{requestorInfo?.email || "—"}</div>
                  </div>
                </div>
              </div>
            </div>

            <fieldset className="lq-quote-fieldset" disabled={quoteLocked}>
            {/* Quote Information */}
            <div className="lq-card lq-card--quote">
              <div className="lq-card-head lq-card-head--quote">
                <LogisticsQuoteSectionIcon name="quote" /> Quote Information
              </div>
              <div className="lq-card-body">
              {Object.keys(fieldErrors).length ? (
                <div className="lq-form-errors-summary" role="alert">
                  Please complete all mandatory fields highlighted below before submitting your quote.
                </div>
              ) : null}
              <div className="lq-section-title">
                <LogisticsQuoteSectionIcon name="basicInfo" />
                Basic Information
              </div>
              <div className="lq-field-row-4">
                <div {...fieldWrap("currency")}>
                  <label className="lq-field-label">
                    Currency
                    <MandatoryMark fieldKey="currency" />
                  </label>
                  <select
                    className="lq-field-control lq-field-control--select"
                    value={form.currency}
                    onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value }))}
                  >
                    {currencyLoading ? <option value={form.currency}>Loading…</option> : null}
                    {!currencyLoading
                      ? currencySelectOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))
                      : null}
                  </select>
                  <FieldError fieldKey="currency" />
                </div>
                <div {...fieldWrap("validityDays")}>
                  <label className="lq-field-label">
                    Validity (Days)
                    <MandatoryMark fieldKey="validityDays" />
                  </label>
                  <input
                    type="number"
                    className="lq-field-control"
                    placeholder="30"
                    value={form.validityDays}
                    onChange={(e) => setForm((p) => ({ ...p, validityDays: e.target.value }))}
                  />
                  <FieldError fieldKey="validityDays" />
                </div>
                <div {...fieldWrap("transitDays")}>
                  <label className="lq-field-label">
                    Transit Time (Days)
                    <MandatoryMark fieldKey="transitDays" />
                  </label>
                  <input
                    type="number"
                    className="lq-field-control"
                    placeholder="12"
                    value={form.transitDays}
                    onChange={(e) => setForm((p) => ({ ...p, transitDays: e.target.value }))}
                  />
                  <FieldError fieldKey="transitDays" />
                </div>
                <div {...fieldWrap("quoteExpiryDate")}>
                  <label className="lq-field-label">
                    Quote Expiry Date
                    <MandatoryMark fieldKey="quoteExpiryDate" />
                  </label>
                  <div className="lq-input-icon">
                    <input
                      type="date"
                      className="lq-field-control lq-field-control--icon-right"
                      value={form.quoteExpiryDate}
                      onChange={(e) => setForm((p) => ({ ...p, quoteExpiryDate: e.target.value }))}
                    />
                    <span className="lq-input-icon-right" aria-hidden>
                      <LogisticsQuoteSectionIcon name="calendar" className="lq-input-calendar-icon" />
                    </span>
                  </div>
                  <FieldError fieldKey="quoteExpiryDate" />
                </div>
              </div>

              <div className="lq-divider" />
              <div className="lq-section-title">
                <LogisticsQuoteSectionIcon name="vesselTimeline" />
                Vessel &amp; Timeline
              </div>
              <div className="lq-field-row-4">
                <div {...fieldWrap("vesselName")}>
                  <label className="lq-field-label">
                    Vessel Name
                    <MandatoryMark fieldKey="vesselName" />
                  </label>
                  <input
                    className="lq-field-control"
                    placeholder="Ocean Star"
                    value={form.vesselName}
                    onChange={(e) => setForm((p) => ({ ...p, vesselName: e.target.value }))}
                  />
                  <FieldError fieldKey="vesselName" />
                </div>
                <div {...fieldWrap("voyageNumber")}>
                  <label className="lq-field-label">
                    Voyage Number
                    <MandatoryMark fieldKey="voyageNumber" />
                  </label>
                  <input
                    className="lq-field-control"
                    placeholder="V-2026-045"
                    value={form.voyageNumber}
                    onChange={(e) => setForm((p) => ({ ...p, voyageNumber: e.target.value }))}
                  />
                  <FieldError fieldKey="voyageNumber" />
                </div>
                <div {...fieldWrap("estDeparture")}>
                  <label className="lq-field-label">
                    Est. Departure
                    <MandatoryMark fieldKey="estDeparture" />
                  </label>
                  <div className="lq-input-icon">
                    <input
                      type="date"
                      className="lq-field-control lq-field-control--icon-right"
                      value={form.estDeparture}
                      onChange={(e) => setForm((p) => ({ ...p, estDeparture: e.target.value }))}
                    />
                    <span className="lq-input-icon-right" aria-hidden>
                      <LogisticsQuoteSectionIcon name="calendar" className="lq-input-calendar-icon" />
                    </span>
                  </div>
                  <FieldError fieldKey="estDeparture" />
                </div>
                <div {...fieldWrap("estArrival")}>
                  <label className="lq-field-label">
                    Est. Arrival
                    <MandatoryMark fieldKey="estArrival" />
                  </label>
                  <div className="lq-input-icon">
                    <input
                      type="date"
                      className="lq-field-control lq-field-control--icon-right"
                      value={form.estArrival}
                      onChange={(e) => setForm((p) => ({ ...p, estArrival: e.target.value }))}
                    />
                    <span className="lq-input-icon-right" aria-hidden>
                      <LogisticsQuoteSectionIcon name="calendar" className="lq-input-calendar-icon" />
                    </span>
                  </div>
                  <FieldError fieldKey="estArrival" />
                </div>
              </div>

              <div className="lq-divider" />
              <div className="lq-section-title">
                <LogisticsQuoteSectionIcon name="coreCharges" />
                Core Charges (Included)
              </div>
              <div className="lq-field-row-4">
                <div {...fieldWrap("oceanFreight")}>
                  <label className="lq-field-label">
                    Ocean Freight
                    <MandatoryMark fieldKey="oceanFreight" />
                  </label>
                  <div className="lq-money">
                    <span className="lq-money-prefix">{form.currency || "USD"}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="lq-field-control lq-field-control--money"
                      placeholder="3200"
                      value={form.oceanFreight}
                      onChange={(e) => setForm((p) => ({ ...p, oceanFreight: e.target.value }))}
                    />
                  </div>
                  <FieldError fieldKey="oceanFreight" />
                </div>
                <div {...fieldWrap("fuelSurcharge")}>
                  <label className="lq-field-label">
                    BAF / Fuel Surcharge
                    <MandatoryMark fieldKey="fuelSurcharge" />
                  </label>
                  <div className="lq-money">
                    <span className="lq-money-prefix">{form.currency || "USD"}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="lq-field-control lq-field-control--money"
                      placeholder="200"
                      value={form.fuelSurcharge}
                      onChange={(e) => setForm((p) => ({ ...p, fuelSurcharge: e.target.value }))}
                    />
                  </div>
                  <FieldError fieldKey="fuelSurcharge" />
                </div>
                <div {...fieldWrap("portHandling")}>
                  <label className="lq-field-label">
                    Port Handling
                    <MandatoryMark fieldKey="portHandling" />
                  </label>
                  <div className="lq-money">
                    <span className="lq-money-prefix">{form.currency || "USD"}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="lq-field-control lq-field-control--money"
                      placeholder="450"
                      value={form.portHandling}
                      onChange={(e) => setForm((p) => ({ ...p, portHandling: e.target.value }))}
                    />
                  </div>
                  <FieldError fieldKey="portHandling" />
                </div>
                <div {...fieldWrap("documentation")}>
                  <label className="lq-field-label">
                    Documentation
                    <MandatoryMark fieldKey="documentation" />
                  </label>
                  <div className="lq-money">
                    <span className="lq-money-prefix">{form.currency || "USD"}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="lq-field-control lq-field-control--money"
                      placeholder="150"
                      value={form.documentation}
                      onChange={(e) => setForm((p) => ({ ...p, documentation: e.target.value }))}
                    />
                  </div>
                  <FieldError fieldKey="documentation" />
                </div>
              </div>
              <div className="lq-field-row lq-field-row--spaced-top">
                <div {...fieldWrap("containerDeposit")}>
                  <label className="lq-field-label">
                    Container Deposit (Refundable)
                    <MandatoryMark fieldKey="containerDeposit" />
                  </label>
                  <div className="lq-money lq-money--wide">
                    <span className="lq-money-prefix">{form.currency || "USD"}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="lq-field-control lq-field-control--money"
                      placeholder="500"
                      value={form.containerDeposit}
                      onChange={(e) => setForm((p) => ({ ...p, containerDeposit: e.target.value }))}
                    />
                  </div>
                  <FieldError fieldKey="containerDeposit" />
                </div>
              </div>

              <div className="lq-divider" />
              <div className="lq-section-title">
                <LogisticsQuoteSectionIcon name="loadingUnloading" />
                Loading &amp; Unloading Charges
              </div>

              <div className="lq-lu-card">
                <div className="lq-lu-row">
                  <div {...fieldWrap("loadingCharge")}>
                    <label className="lq-field-label">
                      Loading Charges
                      <MandatoryMark fieldKey="loadingCharge" />
                    </label>
                    <div className="lq-money lq-money--wide">
                      <span className="lq-money-prefix">{form.currency || "USD"}</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="lq-field-control lq-field-control--money"
                        placeholder="300"
                        value={form.loadingCharge}
                        onChange={(e) => setForm((p) => ({ ...p, loadingCharge: e.target.value }))}
                      />
                    </div>
                    <FieldError fieldKey="loadingCharge" />
                  </div>

                  <div>
                    <label className="lq-field-label">Inclusion Status</label>
                    <div className="lq-inc-box">
                      <label className="lq-switch">
                        <input
                          type="checkbox"
                          checked={!!form.loadingIncluded}
                          onChange={(e) => setForm((p) => ({ ...p, loadingIncluded: e.target.checked }))}
                        />
                        <span className="lq-slider" aria-hidden />
                      </label>
                      {form.loadingIncluded ? (
                        <div className="lq-inc-text lq-inc-text--on">
                          <span className="lq-inc-check" aria-hidden>
                            ✓
                          </span>
                          Included in Quote
                        </div>
                      ) : (
                        <div className="lq-inc-text lq-inc-text--off">
                          <span className="lq-inc-plus" aria-hidden>
                            ⊕
                          </span>
                          Additional Charges
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="lq-field-label">Location</label>
                    <input
                      className="lq-field-control"
                      placeholder="Origin Port"
                      value={form.loadingLocation}
                      readOnly
                    />
                  </div>
                </div>
              </div>

              <div className="lq-lu-card">
                <div className="lq-lu-row">
                  <div {...fieldWrap("unloadingCharge")}>
                    <label className="lq-field-label">
                      Unloading Charges
                      <MandatoryMark fieldKey="unloadingCharge" />
                    </label>
                    <div className="lq-money lq-money--wide">
                      <span className="lq-money-prefix">{form.currency || "USD"}</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="lq-field-control lq-field-control--money"
                        placeholder="300"
                        value={form.unloadingCharge}
                        onChange={(e) => setForm((p) => ({ ...p, unloadingCharge: e.target.value }))}
                      />
                    </div>
                    <FieldError fieldKey="unloadingCharge" />
                  </div>

                  <div>
                    <label className="lq-field-label">Inclusion Status</label>
                    <div className="lq-inc-box">
                      <label className="lq-switch">
                        <input
                          type="checkbox"
                          checked={!!form.unloadingIncluded}
                          onChange={(e) => setForm((p) => ({ ...p, unloadingIncluded: e.target.checked }))}
                        />
                        <span className="lq-slider" aria-hidden />
                      </label>
                      {form.unloadingIncluded ? (
                        <div className="lq-inc-text lq-inc-text--on">
                          <span className="lq-inc-check" aria-hidden>
                            ✓
                          </span>
                          Included in Quote
                        </div>
                      ) : (
                        <div className="lq-inc-text lq-inc-text--off">
                          <span className="lq-inc-plus" aria-hidden>
                            ⊕
                          </span>
                          Additional Charges
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="lq-field-label">Location</label>
                    <input
                      className="lq-field-control"
                      placeholder="Destination Port"
                      value={form.unloadingLocation}
                      readOnly
                    />
                  </div>
                </div>
              </div>

              <div className="lq-divider" />
              <div className="lq-section-title">
                <LogisticsQuoteSectionIcon name="additionalServices" />
                Additional Services (Optional)
              </div>

              <div className="lq-subcard lq-subcard--blue">
                <div className="lq-subcard-head">
                  <LogisticsQuoteSectionIcon name="cargoInsurance" /> Cargo Insurance
                  <span className="ms-auto" />
                  <label className="lq-switch lq-switch--sm">
                    <input
                      type="checkbox"
                      checked={!!form.cargoInsuranceOn}
                      onChange={(e) => setForm((p) => ({ ...p, cargoInsuranceOn: e.target.checked }))}
                    />
                    <span className="lq-slider" aria-hidden />
                  </label>
                </div>
                {form.cargoInsuranceOn ? (
                  <div className="lq-subcard-body">
                    <div className="lq-field-row">
                      <div {...fieldWrap("insuranceAmount")}>
                        <label className="lq-field-label">
                          Insurance Amount
                          <MandatoryMark fieldKey="insuranceAmount" />
                        </label>
                        <div className="lq-money lq-money--wide">
                          <span className="lq-money-prefix">{form.currency || "USD"}</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="lq-field-control lq-field-control--money"
                            placeholder="450"
                            value={form.insuranceAmount}
                            onChange={(e) => setForm((p) => ({ ...p, insuranceAmount: e.target.value }))}
                          />
                        </div>
                        <FieldError fieldKey="insuranceAmount" />
                      </div>
                      <div {...fieldWrap("coveragePercent")}>
                        <label className="lq-field-label">
                          Coverage %
                          <MandatoryMark fieldKey="coveragePercent" />
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="lq-field-control"
                          placeholder="2"
                          value={form.coveragePercent}
                          onChange={(e) => setForm((p) => ({ ...p, coveragePercent: e.target.value }))}
                        />
                        <FieldError fieldKey="coveragePercent" />
                      </div>
                      <div {...fieldWrap("coverageType")}>
                        <label className="lq-field-label">
                          Coverage Type
                          <MandatoryMark fieldKey="coverageType" />
                        </label>
                        <input
                          className="lq-field-control"
                          placeholder="Full Cargo Value"
                          value={form.coverageType}
                          onChange={(e) => setForm((p) => ({ ...p, coverageType: e.target.value }))}
                        />
                        <FieldError fieldKey="coverageType" />
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="lq-subcard lq-subcard--purple">
                <div className="lq-subcard-head">
                  <LogisticsQuoteSectionIcon name="customsClearance" /> Customs Clearance
                  <span className="ms-auto" />
                  <label className="lq-switch lq-switch--sm">
                    <input
                      type="checkbox"
                      checked={!!form.customsOn}
                      onChange={(e) => setForm((p) => ({ ...p, customsOn: e.target.checked }))}
                    />
                    <span className="lq-slider" aria-hidden />
                  </label>
                </div>
                {form.customsOn ? (
                  <div className="lq-subcard-body">
                    <div className="lq-field-row">
                      <div {...fieldWrap("customsClearanceCharge")}>
                        <label className="lq-field-label">
                          Customs Clearance Charges
                          <MandatoryMark fieldKey="customsClearanceCharge" />
                        </label>
                        <div className="lq-money lq-money--wide">
                          <span className="lq-money-prefix">{form.currency || "USD"}</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="lq-field-control lq-field-control--money"
                            placeholder="500"
                            value={form.customsClearanceCharge}
                            onChange={(e) => setForm((p) => ({ ...p, customsClearanceCharge: e.target.value }))}
                          />
                        </div>
                        <FieldError fieldKey="customsClearanceCharge" />
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="lq-subcard lq-subcard--green">
                <div className="lq-subcard-head">
                  <LogisticsQuoteSectionIcon name="warehousing" /> Warehousing / Storage
                  <span className="ms-auto" />
                  <label className="lq-switch lq-switch--sm">
                    <input
                      type="checkbox"
                      checked={!!form.warehousingOn}
                      onChange={(e) => setForm((p) => ({ ...p, warehousingOn: e.target.checked }))}
                    />
                    <span className="lq-slider" aria-hidden />
                  </label>
                </div>
                {form.warehousingOn ? (
                  <div className="lq-subcard-body">
                    <div className="lq-field-row">
                      <div {...fieldWrap("freeDaysIncluded")}>
                        <label className="lq-field-label">
                          Free Days Included
                          <MandatoryMark fieldKey="freeDaysIncluded" />
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className="lq-field-control"
                          placeholder="7"
                          value={form.freeDaysIncluded}
                          onChange={(e) => setForm((p) => ({ ...p, freeDaysIncluded: e.target.value }))}
                        />
                        <FieldError fieldKey="freeDaysIncluded" />
                      </div>
                      <div {...fieldWrap("baseStorageCharge")}>
                        <label className="lq-field-label">
                          Base Storage Charge
                          <MandatoryMark fieldKey="baseStorageCharge" />
                        </label>
                        <div className="lq-money lq-money--wide">
                          <span className="lq-money-prefix">{form.currency || "USD"}</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="lq-field-control lq-field-control--money"
                            placeholder="200"
                            value={form.baseStorageCharge}
                            onChange={(e) => setForm((p) => ({ ...p, baseStorageCharge: e.target.value }))}
                          />
                        </div>
                        <FieldError fieldKey="baseStorageCharge" />
                      </div>
                      <div {...fieldWrap("perDayChargeAfterFree")}>
                        <label className="lq-field-label">
                          Per Day Charge (After Free Days)
                          <MandatoryMark fieldKey="perDayChargeAfterFree" />
                        </label>
                        <div className="lq-money lq-money--wide">
                          <span className="lq-money-prefix">{form.currency || "USD"}</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="lq-field-control lq-field-control--money"
                            placeholder="50"
                            value={form.perDayChargeAfterFree}
                            onChange={(e) => setForm((p) => ({ ...p, perDayChargeAfterFree: e.target.value }))}
                          />
                        </div>
                        <FieldError fieldKey="perDayChargeAfterFree" />
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="lq-subcard lq-subcard--yellow">
                <div className="lq-subcard-head">
                  <LogisticsQuoteSectionIcon name="pickup" /> Pickup Service
                  <span className="ms-auto" />
                  <label className="lq-switch lq-switch--sm">
                    <input
                      type="checkbox"
                      checked={!!form.pickupOn}
                      onChange={(e) => setForm((p) => ({ ...p, pickupOn: e.target.checked }))}
                    />
                    <span className="lq-slider" aria-hidden />
                  </label>
                </div>
                {form.pickupOn ? (
                  <div className="lq-subcard-body">
                    <div className="lq-field-row">
                      <div {...fieldWrap("pickupLocation")}>
                        <label className="lq-field-label">
                          Pickup Location
                          <MandatoryMark fieldKey="pickupLocation" />
                        </label>
                        <input
                          className="lq-field-control"
                          placeholder="Enter pickup address"
                          value={form.pickupLocation}
                          onChange={(e) => setForm((p) => ({ ...p, pickupLocation: e.target.value }))}
                        />
                        <FieldError fieldKey="pickupLocation" />
                      </div>
                      <div {...fieldWrap("pickupCharge")}>
                        <label className="lq-field-label">
                          Pickup Charges
                          <MandatoryMark fieldKey="pickupCharge" />
                        </label>
                        <div className="lq-money lq-money--wide">
                          <span className="lq-money-prefix">{form.currency || "USD"}</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="lq-field-control lq-field-control--money"
                            placeholder="250"
                            value={form.pickupCharge}
                            onChange={(e) => setForm((p) => ({ ...p, pickupCharge: e.target.value }))}
                          />
                        </div>
                        <FieldError fieldKey="pickupCharge" />
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="lq-subcard lq-subcard--teal">
                <div className="lq-subcard-head">
                  <LogisticsQuoteSectionIcon name="delivery" /> Delivery Service
                  <span className="ms-auto" />
                  <label className="lq-switch lq-switch--sm">
                    <input
                      type="checkbox"
                      checked={!!form.deliveryOn}
                      onChange={(e) => setForm((p) => ({ ...p, deliveryOn: e.target.checked }))}
                    />
                    <span className="lq-slider" aria-hidden />
                  </label>
                </div>
                {form.deliveryOn ? (
                  <div className="lq-subcard-body">
                    <div className="lq-field-row">
                      <div {...fieldWrap("deliveryLocation")}>
                        <label className="lq-field-label">
                          Delivery Location
                          <MandatoryMark fieldKey="deliveryLocation" />
                        </label>
                        <input
                          className="lq-field-control"
                          placeholder="Enter delivery address"
                          value={form.deliveryLocation}
                          onChange={(e) => setForm((p) => ({ ...p, deliveryLocation: e.target.value }))}
                        />
                        <FieldError fieldKey="deliveryLocation" />
                      </div>
                      <div {...fieldWrap("deliveryCharge")}>
                        <label className="lq-field-label">
                          Delivery Charges
                          <MandatoryMark fieldKey="deliveryCharge" />
                        </label>
                        <div className="lq-money lq-money--wide">
                          <span className="lq-money-prefix">{form.currency || "USD"}</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="lq-field-control lq-field-control--money"
                            placeholder="250"
                            value={form.deliveryCharge}
                            onChange={(e) => setForm((p) => ({ ...p, deliveryCharge: e.target.value }))}
                          />
                        </div>
                        <FieldError fieldKey="deliveryCharge" />
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="lq-divider" />
              <div className="lq-section-title">
                <LogisticsQuoteSectionIcon name="freeTimeDetention" />
                Free Time &amp; Detention Charges
              </div>
              <div className="lq-field-row-4">
                <div {...fieldWrap("freeDaysOrigin")}>
                  <label className="lq-field-label">
                    Free Days at Origin
                    <MandatoryMark fieldKey="freeDaysOrigin" />
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="lq-field-control"
                    placeholder="3"
                    value={form.freeDaysOrigin}
                    onChange={(e) => setForm((p) => ({ ...p, freeDaysOrigin: e.target.value }))}
                  />
                  <FieldError fieldKey="freeDaysOrigin" />
                </div>
                <div {...fieldWrap("freeDaysDestination")}>
                  <label className="lq-field-label">
                    Free Days at Destination
                    <MandatoryMark fieldKey="freeDaysDestination" />
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="lq-field-control"
                    placeholder="5"
                    value={form.freeDaysDestination}
                    onChange={(e) => setForm((p) => ({ ...p, freeDaysDestination: e.target.value }))}
                  />
                  <FieldError fieldKey="freeDaysDestination" />
                </div>
                <div {...fieldWrap("detentionPerDay")}>
                  <label className="lq-field-label">
                    Detention per Day
                    <MandatoryMark fieldKey="detentionPerDay" />
                  </label>
                  <div className="lq-money">
                    <span className="lq-money-prefix">{form.currency || "USD"}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="lq-field-control lq-field-control--money"
                      placeholder="100"
                      value={form.detentionPerDay}
                      onChange={(e) => setForm((p) => ({ ...p, detentionPerDay: e.target.value }))}
                    />
                  </div>
                  <FieldError fieldKey="detentionPerDay" />
                </div>
                <div {...fieldWrap("demurragePerDay")}>
                  <label className="lq-field-label">
                    Demurrage per Day
                    <MandatoryMark fieldKey="demurragePerDay" />
                  </label>
                  <div className="lq-money">
                    <span className="lq-money-prefix">{form.currency || "USD"}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="lq-field-control lq-field-control--money"
                      placeholder="150"
                      value={form.demurragePerDay}
                      onChange={(e) => setForm((p) => ({ ...p, demurragePerDay: e.target.value }))}
                    />
                  </div>
                  <FieldError fieldKey="demurragePerDay" />
                </div>
              </div>

              <div className="lq-divider" />
              <div className="lq-section-title">
                <LogisticsQuoteSectionIcon name="paymentTerms" />
                Payment Terms &amp; Additional Information
              </div>
              <div className="lq-two-col">
                <div {...fieldWrap("paymentTerms")}>
                  <label className="lq-field-label">
                    Payment Terms
                    <MandatoryMark fieldKey="paymentTerms" />
                  </label>
                  <select
                    className="lq-field-control lq-field-control--select"
                    value={form.paymentTerms}
                    onChange={(e) => setForm((p) => ({ ...p, paymentTerms: e.target.value }))}
                  >
                    {paymentTermsLoading ? <option value={form.paymentTerms}>Loading…</option> : null}
                    {!paymentTermsLoading
                      ? paymentTermSelectOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))
                      : null}
                  </select>
                  <FieldError fieldKey="paymentTerms" />
                </div>
                <div {...fieldWrap("exclusions")}>
                  <label className="lq-field-label">
                    Exclusions
                    <MandatoryMark fieldKey="exclusions" />
                  </label>
                  <input
                    className="lq-field-control"
                    value={form.exclusions}
                    onChange={(e) => setForm((p) => ({ ...p, exclusions: e.target.value }))}
                  />
                  <FieldError fieldKey="exclusions" />
                </div>
              </div>
              <div className="lq-two-col" style={{ marginTop: 10 }}>
                <div>
                  <label className="lq-field-label">Special Terms &amp; Conditions</label>
                  <input
                    className="lq-field-control"
                    placeholder="Any special conditions..."
                    value={form.specialTerms}
                    onChange={(e) => setForm((p) => ({ ...p, specialTerms: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="lq-field-label">Additional Notes</label>
                  <input
                    className="lq-field-control"
                    placeholder="Any additional information for the client..."
                    value={form.additionalNotes}
                    onChange={(e) => setForm((p) => ({ ...p, additionalNotes: e.target.value }))}
                  />
                </div>
              </div>
              <div {...fieldWrap("generalTerms")} style={{ marginTop: 10 }}>
                <label className="lq-field-label">
                  General Terms &amp; Conditions
                  <MandatoryMark fieldKey="generalTerms" />
                </label>
                <input
                  className="lq-field-control"
                  value={form.generalTerms}
                  onChange={(e) => setForm((p) => ({ ...p, generalTerms: e.target.value }))}
                />
                <FieldError fieldKey="generalTerms" />
              </div>
            </div>
          </div>
            </fieldset>
          </div>

          {/* Right Quote Summary panel */}
          <div className="lq-right-sticky">
            <div className="lq-quote-summary">
              <div className="lq-quote-summary-head">
                <LogisticsQuoteSectionIcon name="quoteSummary" />
                Quote Summary
              </div>
              <div className="p-3">
                <div className="lq-total-bar" style={{ marginTop: 0 }}>
                  <div className="lq-total-left">
                    <div className="lq-total-label">Total Quote Amount</div>
                    <div className="lq-total-value">
                      {form.currency} {Math.max(0, totalQuoteAmount).toFixed(0)}
                    </div>
                  </div>
                  <div className="lq-total-right">
                    <div className="opacity-90 small">Transit</div>
                    <div>{toNum(form.transitDays) || 0} days</div>
                  </div>
                </div>

                <div className="lq-quote-actions">
                  {quoteLocked ? (
                    <p className="lq-quote-locked-note mb-0">
                      This quote is {statusLabel ? statusLabel.toLowerCase() : "locked"} and cannot be edited.
                    </p>
                  ) : (
                    <>
                      <button type="button" className="lq-btn-submit" onClick={handleSubmitQuote} disabled={submitting}>
                        Submit Quote
                      </button>
                      <button type="button" className="lq-btn-draft" onClick={handleSaveDraft} disabled={savingDraft}>
                        Save as Draft
                      </button>
                    </>
                  )}
                </div>

                <div className="lq-checklist">
                  <div className="lq-checklist-title">
                    <LogisticsQuoteSectionIcon name="checklist" />
                    Checklist
                  </div>
                  {[
                    "All charges filled",
                    "Loading/Unloading inclusion clearly marked",
                    "Warehouse charges include any free days",
                    "Free time & detention specified",
                    "Exclusions clearly mentioned",
                  ].map((t) => (
                    <div className="lq-check-item" key={t}>
                      <span className="lq-check-dot" aria-hidden />
                      <span>{t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

